// @ts-check
// Textos e listas das automações na interface: resumo do gatilho, situação das execuções, campos elegíveis por papel.
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/automations.js').Automation} Automation
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 */

export const TRIGGER_TYPES = /** @type {const} */ (['RECORD_CREATED', 'FIELD_CHANGED', 'STAGE_CHANGED', 'RECORD_DELETED', 'DATE_APPROACHING', 'RECORD_STALE', 'MANUAL', 'SCHEDULED', 'WEBHOOK']);
export const ACTION_TYPES = /** @type {const} */ ([
  'CREATE_TASK', 'ASSIGN_OWNER', 'UPDATE_FIELD', 'UPDATE_RECORD', 'CREATE_RECORD', 'FIND_RECORDS', 'IF_ELSE', 'CREATE_CALENDAR_EVENT', 'SEND_EMAIL',
  'SEND_WHATSAPP_TEMPLATE', 'SEND_NOTIFICATION', 'CALL_WEBHOOK', 'HTTP_REQUEST', 'WAIT',
]);
// ações que mudam o registro não existem no gatilho de exclusão (o motor recusa)
export const ACTIONS_BLOCKED_ON_DELETED_RECORD = new Set(['ASSIGN_OWNER', 'UPDATE_FIELD']);
// sem registro (webhook, agendamento "uma vez") estas ações não têm onde agir
export const ACTIONS_NEEDING_RECORD = new Set(['ASSIGN_OWNER', 'UPDATE_FIELD', 'SEND_WHATSAPP_TEMPLATE']);
// se/senão dentro de se/senão: dois níveis (o motor recusa mais)
export const MAXIMUM_BRANCH_DEPTH = 2;
export const TRIGGER_ICONS = Object.freeze({
  RECORD_CREATED: 'IconCirclePlus', FIELD_CHANGED: 'IconRefresh', STAGE_CHANGED: 'IconProgressCheck', RECORD_DELETED: 'IconTrash',
  DATE_APPROACHING: 'IconCalendarClock', RECORD_STALE: 'IconHourglass', MANUAL: 'IconHandMove', SCHEDULED: 'IconClock', WEBHOOK: 'IconWebhook',
});
export const ACTION_ICONS = Object.freeze({
  CREATE_TASK: 'IconCheckbox', ASSIGN_OWNER: 'IconUserCheck', UPDATE_FIELD: 'IconPencil', SEND_EMAIL: 'IconMail', SEND_WHATSAPP_TEMPLATE: 'IconBrandWhatsapp',
  SEND_NOTIFICATION: 'IconBell', CALL_WEBHOOK: 'IconWebhook', WAIT: 'IconPlayerPause', IF_ELSE: 'IconSitemap', CREATE_RECORD: 'IconCirclePlus',
  UPDATE_RECORD: 'IconEdit', FIND_RECORDS: 'IconSearch', CREATE_CALENDAR_EVENT: 'IconCalendarPlus', HTTP_REQUEST: 'IconWorld',
});
export const RUN_STATUS_COLORS = Object.freeze({
  NOT_STARTED: 'gray', ENQUEUED: 'sky', RUNNING: 'blue', WAITING: 'purple', COMPLETED: 'green', FAILED: 'red', SKIPPED: 'gray', STOPPED: 'orange',
});
// execuções que ainda podem ser paradas pela tela de histórico
export const STOPPABLE_RUN_STATUSES = new Set(['NOT_STARTED', 'ENQUEUED', 'RUNNING', 'WAITING']);
export const STEP_STATUS_COLORS = Object.freeze({
  NOT_STARTED: 'gray', PENDING: 'sky', WAITING: 'purple', SUCCESS: 'green', FAILED: 'red', ROLLED_BACK: 'orange', SKIPPED: 'gray', STOPPED: 'orange',
});

// objetos que o motor recusa em automações (junções e membros)
const BLOCKED_OBJECTS = new Set(['taskTarget', 'noteTarget', 'workspaceMember', 'attachment']);
const READ_ONLY_FIELD_NAMES = new Set(['id', 'createdAt', 'updatedAt', 'deletedAt', 'createdBy', 'updatedBy', 'position', 'searchVector']);
export const UPDATABLE_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'DATE_TIME', 'DATE', 'RATING', 'RELATION']);
// campos que as ações genéricas de registro sabem preencher pela tela (os compostos entram como texto)
export const RECORD_ACTION_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT', 'DATE_TIME', 'DATE', 'RATING', 'RELATION', 'FULL_NAME', 'EMAILS', 'PHONES', 'LINKS']);
export const CONDITION_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT', 'DATE_TIME', 'DATE', 'RATING', 'RELATION', 'UUID']);

/** @param {string} triggerType @param {Record<string, any> | null | undefined} settings */
export const isRecordlessTrigger = (triggerType, settings) => triggerType === 'WEBHOOK' || (triggerType === 'SCHEDULED' && settings?.mode === 'ONCE');

/** @param {ObjectMetadata[]} objects */
export function automationObjects(objects) {
  return objects
    .filter((objectMetadata) => objectMetadata.isActive !== false && !BLOCKED_OBJECTS.has(objectMetadata.nameSingular)
      && (!objectMetadata.isSystem || objectMetadata.nameSingular === 'task'))
    .sort((first, second) => translateMetadataLabel(first.labelPlural).localeCompare(translateMetadataLabel(second.labelPlural)));
}

/** @param {FieldMetadata} field */
export const isMemberRelation = (field) => field.type === 'RELATION' && field.relation?.type === 'MANY_TO_ONE'
  && field.relation?.targetObjectNameSingular === 'workspaceMember';

/** @param {FieldMetadata} field */
export const isManyToOne = (field) => field.type === 'RELATION' && field.relation?.type === 'MANY_TO_ONE';

/** Chave do campo no registro: `ownerId` para relações. @param {FieldMetadata} field */
export const recordKeyOf = (field) => (field.type === 'RELATION' ? `${field.name}Id` : field.name);

/** @param {ObjectMetadata | undefined} objectMetadata @param {(field: FieldMetadata) => boolean} predicate */
export function pickFields(objectMetadata, predicate) {
  return (objectMetadata?.fields ?? [])
    .filter((field) => field.isActive !== false && predicate(field))
    .sort((first, second) => translateMetadataLabel(first.label).localeCompare(translateMetadataLabel(second.label)));
}

/** Campos que podem disparar "campo alterado" ou entrar em condições. @param {FieldMetadata} field */
export const isWatchableField = (field) => !READ_ONLY_FIELD_NAMES.has(field.name) && field.type !== 'TS_VECTOR' && field.type !== 'POSITION'
  && (field.type !== 'RELATION' || isManyToOne(field));

// datas que o motor preenche sozinho não servem para "data chegando" nem para prazo de tarefa
const ENGINE_DATE_FIELD_NAMES = new Set(['createdAt', 'updatedAt', 'deletedAt']);

/** Data futura do registro (visita, prova): base de lembretes e prazos. @param {FieldMetadata} field */
export const isScheduleDateField = (field) => (field.type === 'DATE_TIME' || field.type === 'DATE') && !ENGINE_DATE_FIELD_NAMES.has(field.name) && !field.isUIReadOnly;

/** @param {FieldMetadata} field */
export const isUpdatableField = (field) => !field.isSystem && !field.isUIReadOnly && !READ_ONLY_FIELD_NAMES.has(field.name)
  && UPDATABLE_FIELD_TYPES.has(field.type) && (field.type !== 'RELATION' || isMemberRelation(field));

/** Campo que CREATE_RECORD e UPDATE_RECORD preenchem (relações muitos-para-um, com o id). @param {FieldMetadata} field */
export const isRecordActionField = (field) => !field.isSystem && !field.isUIReadOnly && !READ_ONLY_FIELD_NAMES.has(field.name)
  && RECORD_ACTION_FIELD_TYPES.has(field.type) && (field.type !== 'RELATION' || isManyToOne(field));

/** @param {ObjectMetadata | undefined} objectMetadata @param {string} name */
export function findField(objectMetadata, name) {
  return objectMetadata?.fields.find((field) => field.name === name || recordKeyOf(field) === name);
}

/** @param {ObjectMetadata | undefined} objectMetadata @param {string} name */
export function fieldLabel(objectMetadata, name) {
  const field = findField(objectMetadata, name);
  return field ? translateMetadataLabel(field.label) : name;
}

/** @param {ObjectMetadata | undefined} objectMetadata @param {string} fieldName @param {string} value */
function optionLabel(objectMetadata, fieldName, value) {
  const option = findField(objectMetadata, fieldName)?.options?.find((item) => item.value === value);
  return option ? translateMetadataLabel(option.label) : value;
}

/**
 * Frase curta do gatilho, para a lista.
 * @param {Pick<Automation, 'objectNameSingular' | 'triggerType' | 'triggerSettings'>} automation
 */
export function describeTrigger(automation) {
  const objectMetadata = objectsByNameSingular.value.get(automation.objectNameSingular);
  const objectLabel = objectMetadata ? translateMetadataLabel(objectMetadata.labelSingular) : automation.objectNameSingular;
  const settings = automation.triggerSettings ?? {};
  const prefix = `automations.triggerSummary.${automation.triggerType}`;
  switch (automation.triggerType) {
    case 'FIELD_CHANGED':
      return t(prefix, { object: objectLabel, fields: (settings.fieldNames ?? []).map((name) => fieldLabel(objectMetadata, name)).join(', ') });
    case 'STAGE_CHANGED': {
      const targets = (settings.toValues ?? []).map((value) => optionLabel(objectMetadata, settings.fieldName ?? 'stage', value)).join(', ');
      return targets ? t(`${prefix}To`, { object: objectLabel, field: fieldLabel(objectMetadata, settings.fieldName ?? 'stage'), values: targets })
        : t(prefix, { object: objectLabel, field: fieldLabel(objectMetadata, settings.fieldName ?? 'stage') });
    }
    case 'DATE_APPROACHING':
      return t(prefix, { object: objectLabel, field: fieldLabel(objectMetadata, settings.fieldName), amount: settings.amount, unit: t(`automations.units.${settings.unit ?? 'HOURS'}`, { count: settings.amount }) });
    case 'RECORD_STALE':
      return t(prefix, { object: objectLabel, days: settings.days });
    case 'SCHEDULED':
      return t(`${prefix}.${settings.mode === 'ONCE' ? 'ONCE' : 'RECORDS'}`, { object: objectLabel, when: describeSchedule(settings) });
    default:
      return t(prefix, { object: objectLabel });
  }
}

/** "todo dia às 08:00", "toda terça às 07:00", "todo dia 5 às 09:00". @param {Record<string, any>} settings */
export function describeSchedule(settings) {
  const time = settings.time ?? '08:00';
  if (settings.frequency === 'WEEKLY') return t('automations.schedule.summaryWeekly', { weekday: t(`automations.schedule.weekdays.${settings.weekday ?? 1}`), time });
  if (settings.frequency === 'MONTHLY') return t('automations.schedule.summaryMonthly', { day: settings.dayOfMonth ?? 1, time });
  return t('automations.schedule.summaryDaily', { time });
}
